import {
  ragResultToolDef,
  type RagResultToolDef,
} from '@kanbu/schema/contracts';
import { SourcesDisplayMode, SourcesFormat } from '@kanbu/schema/enums';
import { useLingui } from '@lingui/react/macro';

import { filterRagSources, ragDisplayContent } from './rag-result-utils';
import { Message } from '../../components';
import { SourcesBlock } from '../../components/sources';
import { Markdown } from '../../ui';
import { createToolComponent } from '../tool-registry';

export interface RagResultToolProps {
  sourcesFormat?: SourcesFormat;
  sourcesDisplayMode?: SourcesDisplayMode;
}

/**
 * Legacy final-answer renderer for persisted rows that store the answer
 * inside a synthetic `rag_result` tool call. New turns render natively on
 * the message row; this component remains for old threads.
 */
export const RagResultTool = createToolComponent<
  RagResultToolProps,
  typeof ragResultToolDef
>(
  {
    schema: {
      args: ragResultToolDef,
    },
  },
  ({
    toolCall,
    state,
    sourcesFormat = SourcesFormat.Simple,
    sourcesDisplayMode = SourcesDisplayMode.Pills,
  }) => {
    const { t } = useLingui();
    const { content, sources = [] } = toolCall.args;
    const isComplete = state === 'call-complete' || state === 'result';

    /**
     * Guarantee a visible message once the response is complete. Some models
     * occasionally return empty content (e.g. empty knowledge base), which
     * would otherwise render as a blank bubble stuck on the typing indicator.
     */
    const displayContent = ragDisplayContent(content, {
      isComplete,
      emptyFallback: t`Sorry, I don't have that information available at the moment.`,
    });

    const filteredSources = filterRagSources(sources);

    return (
      <Message.BubbleGroup>
        <Message.Bubble>
          <Markdown citations={filteredSources}>{displayContent}</Markdown>
          {sourcesDisplayMode === SourcesDisplayMode.Pills && (
            <SourcesBlock
              sources={filteredSources}
              sourcesFormat={sourcesFormat}
              sourcesDisplayMode={SourcesDisplayMode.Pills}
            />
          )}
        </Message.Bubble>
        {sourcesDisplayMode === SourcesDisplayMode.Structured &&
          filteredSources.length > 0 && (
            <Message.Bubble>
              <SourcesBlock
                sources={filteredSources}
                sourcesFormat={sourcesFormat}
                sourcesDisplayMode={SourcesDisplayMode.Structured}
              />
            </Message.Bubble>
          )}
      </Message.BubbleGroup>
    );
  },
);

export type { RagResultToolDef };
